import { Accordion, AccordionTab } from 'primereact/accordion';
import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { PERMISSION_DESC, PERMISSION_NAME } from '../../../../../constants/strings';
import { _Permission, _PermissionGroup } from '../../../../../constants/staticTypes';
import { useTranslation } from '../../../../../hooks/useTranslation';

type _VenuePermissionDetailsProps = {
    activeGroupPermission: _PermissionGroup;
    setActiveGroupPermission: Dispatch<SetStateAction<_PermissionGroup | null>>;
    activeGroupPermissionList: _Permission[] | null | undefined;
    onPermissionChangeEvent: (event: ChangeEvent<HTMLInputElement>, permission: _Permission) => void;
};

const languageTexts = {
    PERMISSION_NAME: PERMISSION_NAME,
    PERMISSION_DESC: PERMISSION_DESC,
};

// Group permissions by module
const getGroupedPermissions = (permissions: _Permission[] | null | undefined) => {
    if (!permissions) return [];

    const grouped = new Map<string, _Permission[]>();
    permissions.forEach((permission) => {
        const moduleName = permission.moduleName || 'Other';
        if (!grouped.has(moduleName)) {
            grouped.set(moduleName, []);
        }
        if (!permission.parentPermissionId) {
            grouped.get(moduleName)?.push(permission);
        }
    });

    return Array.from(grouped.entries()).map(([moduleName, perms]) => ({
        moduleName,
        permissions: perms,
    }));
};

function VenuePermissionDetails({
    activeGroupPermission,
    setActiveGroupPermission,
    activeGroupPermissionList,
    onPermissionChangeEvent,
}: Readonly<_VenuePermissionDetailsProps>) {
    const translatedText = useTranslation(languageTexts, 'pascal');

    return (
        <div className="col-md-12" data-testid={activeGroupPermission.id + '#selected'}>
            <div className="mb-3">
                <input
                    type="text"
                    className="form-control"
                    id="roleName"
                    data-testid="role-name-input"
                    placeholder={translatedText.PERMISSION_NAME}
                    autoComplete="off"
                    onChange={(e) => {
                        if (activeGroupPermission && e.target.value) {
                            setActiveGroupPermission({
                                ...activeGroupPermission,
                                permissionRoleName: e.target.value,
                            });
                        }
                    }}
                    value={activeGroupPermission?.permissionRoleName || ''}
                />
            </div>
            <div className="mb-3">
                <textarea
                    className="form-control"
                    id="roleDescription"
                    placeholder={translatedText.PERMISSION_DESC}
                    data-testid="role-desc-input"
                    onChange={(e) => {
                        if (activeGroupPermission && e.target.value) {
                            setActiveGroupPermission({
                                ...activeGroupPermission,
                                permissionRoleDescription: e.target.value,
                            });
                        }
                    }}
                    value={activeGroupPermission?.permissionRoleDescription || ''}
                    rows={4}
                ></textarea>
            </div>

            <div className="p-1 mt-2">
                {getGroupedPermissions(activeGroupPermissionList).map((moduleGroup, moduleIndex) => (
                    <div key={moduleGroup.moduleName}>
                        {moduleGroup.permissions.length > 1 && (
                            <div className="d-flex align-items-center my-2" style={{ gap: '0.5rem' }}>
                                <hr className="flex-grow-1 my-0" style={{ borderTop: '1px solid #dee2e6' }} />
                                <span
                                    style={{
                                        fontSize: '0.85rem',
                                        color: '#666',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {moduleGroup.moduleName}
                                </span>
                                <hr className="flex-grow-1 my-0" style={{ borderTop: '1px solid #dee2e6' }} />
                            </div>
                        )}

                        <Accordion activeIndex={moduleIndex}>
                            {moduleGroup.permissions.map((feature) => (
                                <AccordionTab
                                    key={feature?.permissionKey}
                                    data-testid={feature?.permissionKey + '#permission'}
                                    header={
                                        <div className={`d-flex align-items-center`}>
                                            <span className="form-check form-switch">
                                                <input
                                                    key={feature?.permissionKey}
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    autoComplete="off"
                                                    checked={feature?.isActive}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => onPermissionChangeEvent(e, feature)}
                                                />
                                            </span>
                                            <span className="permission-name text-black">{feature?.permissionName}</span>
                                        </div>
                                    }
                                    headerClassName={feature?.subPermissions.length > 0 ? '' : 'accordion-no-children'}
                                    contentClassName={feature?.subPermissions.length > 0 ? '' : 'd-none'}
                                >
                                    {feature?.subPermissions.length > 0 && (
                                        <div>
                                            {feature?.subPermissions?.map((permission: _Permission) => (
                                                <div key={`${permission?.permissionKey}`} className="d-flex mb-3">
                                                    <div className="flex-grow-0">
                                                        <div className="">
                                                            <span className="form-check form-switch">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    autoComplete="off"
                                                                    checked={permission?.isActive}
                                                                    onChange={(e) => onPermissionChangeEvent(e, permission)}
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex-grow-1">{permission.permissionName}</div>
                                                    <div className=""></div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </AccordionTab>
                            ))}
                        </Accordion>

                        {moduleGroup.permissions.length > 1 && <hr className="my-2" style={{ borderTop: '1px solid #dee2e6' }} />}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default VenuePermissionDetails;
